import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { BlockOutlined, DeleteOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';
import { MOTIVOS_DE_DESCARTE, rotuloDoDescarte } from '../../utils/motivoDoDescarte';

/**
 * Remetentes bloqueados, e o que a triagem barrou.
 *
 * Gisele, 27/10/2026:
 *
 *  - **6**: "se possível, identificar spam automaticamente, para não criar
 *    tickets à toa";
 *  - **7**: "opção para o usuário/admin marcar um remetente como
 *    Spam/Bloqueado, para que os próximos e-mails dele não gerem ticket".
 *
 * ## Por que as duas listas ficam na MESMA tela
 *
 * Bloquear é fácil; descobrir que se bloqueou o endereço errado, não. A lista
 * do que foi descartado é o contraponto do botão de bloquear: é por ela que
 * alguém vê o e-mail do cliente que foi barrado por engano -- e o contador de
 * cada bloqueio responde "adiantou?" sem ninguém precisar adivinhar.
 */
const RemetentesBloqueados = () => {
    const { theme } = useContext(ThemeContext);
    const [bloqueados, setBloqueados] = useState([]);
    const [descartados, setDescartados] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [novoEmail, setNovoEmail] = useState('');
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(() => {
        setCarregando(true);

        Promise.all([
            http.get('/remetentes-bloqueados'),
            http.get('/emails-descartados', { params: { per_page: 50 } }),
        ])
            .then(([lista, barrados]) => {
                setBloqueados(lista.data || []);
                setDescartados(barrados.data?.data || []);
            })
            .catch(() => toast.error('Não foi possível carregar os remetentes bloqueados.'))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const bloquear = () => {
        const email = novoEmail.trim();

        if (!email) return;

        setSalvando(true);

        http.post('/remetentes-bloqueados', { email, motivo: motivo.trim() || null })
            .then(() => {
                setNovoEmail('');
                setMotivo('');
                toast.success('Remetente bloqueado. Os próximos e-mails dele não viram chamado.');
                carregar();
            })
            .catch((erro) => {
                // O 422 da validação é o caso comum (endereço digitado errado),
                // e merece a frase do servidor em vez de um "erro" genérico.
                const detalhe = erro?.response?.data?.errors?.email?.[0];
                toast.error(detalhe || 'Não foi possível bloquear este remetente.');
            })
            .finally(() => setSalvando(false));
    };

    const desbloquear = (bloqueio) => {
        http.delete(`/remetentes-bloqueados/${bloqueio.id}`)
            .then(() => {
                toast.success('Remetente desbloqueado.');
                carregar();
            })
            .catch(() => toast.error('Não foi possível desbloquear.'));
    };

    const dataHora = (valor) => (valor ? new Date(valor).toLocaleString('pt-BR') : '—');

    return (
        <Box data-testid="remetentes-bloqueados">
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                E-mails bloqueados
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                E-mails destes remetentes não geram chamado. A triagem automática também barra
                o que o servidor de e-mail já marcou como spam e as mensagens geradas por
                máquina, como retornos e respostas automáticas.
            </Typography>

            <Paper sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.paper }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                        size="small"
                        label="E-mail do remetente"
                        value={novoEmail}
                        onChange={(e) => setNovoEmail(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        size="small"
                        label="Motivo (opcional)"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<BlockOutlined />}
                        onClick={bloquear}
                        disabled={salvando || novoEmail.trim() === ''}
                    >
                        Bloquear
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ height: 3, mb: 1 }}>
                {carregando && <LinearProgress data-testid="carregando-bloqueados" sx={{ height: 3 }} />}
            </Box>

            <TableContainer component={Paper} sx={{ mb: 4, backgroundColor: theme.palette.background.paper }}>
                <Table size="small" data-testid="lista-de-bloqueados">
                    <TableHead>
                        <TableRow>
                            <TableCell>Remetente</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell>Bloqueado por</TableCell>
                            <TableCell align="right">E-mails barrados</TableCell>
                            <TableCell>Último</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bloqueados.length === 0 && !carregando && (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ color: theme.palette.text.secondary }}>
                                    Nenhum remetente bloqueado.
                                </TableCell>
                            </TableRow>
                        )}
                        {bloqueados.map((item) => (
                            <TableRow key={item.id} hover>
                                <TableCell sx={{ color: theme.palette.text.primary }}>{item.email}</TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>{item.motivo || '—'}</TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {item.quem_bloqueou?.name || '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: theme.palette.text.primary }}>
                                    {item.bloqueios}
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {dataHora(item.ultimo_bloqueio_em)}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Desbloquear" arrow>
                                        <IconButton
                                            size="small"
                                            aria-label={`Desbloquear ${item.email}`}
                                            onClick={() => desbloquear(item)}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                E-mails que não viraram chamado
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Os últimos 50. Esta lista existe para que nada desapareça em silêncio: é por
                ela que se descobre um remetente bloqueado por engano.
            </Typography>

            <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper }}>
                <Table size="small" data-testid="lista-de-descartados">
                    <TableHead>
                        <TableRow>
                            <TableCell>Quando</TableCell>
                            <TableCell>De</TableCell>
                            <TableCell>Assunto</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {descartados.length === 0 && !carregando && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ color: theme.palette.text.secondary }}>
                                    Nenhum e-mail foi descartado.
                                </TableCell>
                            </TableRow>
                        )}
                        {descartados.map((item) => (
                            <TableRow key={item.id} hover>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {dataHora(item.created_at)}
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary }}>
                                    {item.nome_de ? `${item.nome_de} <${item.de}>` : item.de}
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.secondary }}>{item.assunto || '—'}</TableCell>
                                <TableCell>
                                    <Tooltip title={item.detalhe || ''} arrow>
                                        <Chip size="small" label={rotuloDoDescarte(item.motivo)} />
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    {/*
                                      * Bloquear a partir do que já foi barrado: é o caminho
                                      * real: a pessoa vê o spam na lista e quer que ele pare,
                                      * sem redigitar o endereço (e sem errar ao redigitar).
                                      */}
                                    {item.motivo !== MOTIVOS_DE_DESCARTE.REMETENTE_BLOQUEADO && item.de && (
                                        <Tooltip title="Bloquear este remetente" arrow>
                                            <IconButton
                                                size="small"
                                                aria-label={`Bloquear ${item.de}`}
                                                onClick={() => {
                                                    setNovoEmail(item.de);
                                                    setMotivo(rotuloDoDescarte(item.motivo));
                                                }}
                                            >
                                                <BlockOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default RemetentesBloqueados;
